/**
 * Shared attendance helpers — used by ESS and Employee Management
 */

export const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const monthDateRange = (year, month) => {
  const y = year ?? new Date().getFullYear();
  const m = month ?? new Date().getMonth();
  const start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();
  const end = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end };
};

export const isPresentRecord = (record) => {
  const status = record?.status;
  if (status === 'present' || status === 'late' || status === 'half_day') return true;
  if (status === 'absent') return false;
  return !!record?.clock_in_time;
};

export const computeAttendanceStats = (records) => {
  const stats = { present: 0, absent: 0, late: 0, half_day: 0 };
  (records || []).forEach((record) => {
    const status = record.status || (record.clock_in_time ? 'present' : 'absent');
    if (stats[status] !== undefined) stats[status]++;
    else if (isPresentRecord(record)) stats.present++;
    else stats.absent++;
  });
  return stats;
};

export const computeSummaryFromRecords = (records) => {
  if (!records?.length) {
    return {
      totalDays: 0,
      presentDays: 0,
      absentDays: 0,
      lateDays: 0,
      halfDays: 0,
      totalHours: '0h 0m',
      avgDailyHours: '0h 0m',
      attendanceRate: 0,
    };
  }

  const presentDays = records.filter((r) => isPresentRecord(r)).length;
  const absentDays = records.filter((r) => (r.status || 'absent') === 'absent' && !r.clock_in_time).length;
  const lateDays = records.filter((r) => r.status === 'late').length;
  const halfDays = records.filter((r) => r.status === 'half_day').length;
  const totalDays = records.length;

  let totalMinutes = 0;
  records.forEach((r) => {
    if (r.work_hours) totalMinutes += (parseFloat(r.work_hours) || 0) * 60;
  });

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = Math.round(totalMinutes % 60);
  const avgMinutes = totalDays > 0 ? Math.floor(totalMinutes / totalDays) : 0;

  return {
    totalDays,
    presentDays,
    absentDays,
    lateDays,
    halfDays,
    totalHours: `${totalHours}h ${remainingMinutes}m`,
    avgDailyHours: `${Math.floor(avgMinutes / 60)}h ${avgMinutes % 60}m`,
    attendanceRate: totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0,
  };
};

export const normalizeRecordDate = (record) =>
  record?.attendance_date ? String(record.attendance_date).split('T')[0].split(' ')[0] : null;

export const formatMonthLabel = (dateStr) => {
  if (!dateStr) return '—';
  const part = String(dateStr).split('T')[0];
  const [y, m] = part.split('-').map(Number);
  if (!y || !m) return '—';
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

export const getMonthKey = (dateStr) => {
  const part = String(dateStr).split('T')[0];
  return part.length >= 7 ? part.slice(0, 7) : 'unknown';
};

export const groupRecordsByMonth = (records, normalizeDate = normalizeRecordDate) => {
  const groups = new Map();
  (records || []).forEach((record) => {
    const dateStr = normalizeDate(record);
    const key = dateStr ? getMonthKey(dateStr) : 'unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(record);
  });

  return [...groups.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([monthKey, monthRecords]) => ({
      monthKey,
      monthLabel: formatMonthLabel(`${monthKey}-01`),
      records: monthRecords.sort((a, b) => (normalizeDate(b) || '').localeCompare(normalizeDate(a) || '')),
      summary: computeSummaryFromRecords(monthRecords),
    }));
};

/** Full API root including /api suffix — matches apiClient */
export const getApiRoot = () => {
  const configured =
    import.meta.env.VITE_ERP_API_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_API_URL;

  if (configured) {
    return configured.replace(/\/$/, '');
  }

  return '/api';
};

/** Resolve punch state — supports new multi-punch API and legacy todayStatus */
export const getPunchState = (todayStatus) => {
  if (!todayStatus) {
    return { isPunchedIn: false, canPunchIn: true, canPunchOut: false, punchCount: 0 };
  }

  if (todayStatus.can_punch_in !== undefined || todayStatus.can_punch_out !== undefined) {
    return {
      isPunchedIn: !!todayStatus.can_punch_out,
      canPunchIn: !!todayStatus.can_punch_in,
      canPunchOut: !!todayStatus.can_punch_out,
      punchCount: todayStatus.punch_count || 0,
    };
  }

  const isPunchedIn = !!todayStatus.clocked_in && !todayStatus.clocked_out;
  return {
    isPunchedIn,
    canPunchIn: !todayStatus.clocked_in,
    canPunchOut: isPunchedIn,
    punchCount: 0,
  };
};

/** Base URL for backend assets (no trailing /api) */
export const getApiBaseUrl = () => getApiRoot().replace(/\/api\/?$/, '');

/** Build attendance selfie API URL from record id */
export const buildAttendancePhotoUrl = (recordId, type) => {
  if (!recordId || !type) return null;
  const apiRoot = getApiRoot();
  const token = localStorage.getItem('erp_auth_token');
  let url = `${apiRoot}/attendance/${recordId}/photo/${type}`;
  if (token) {
    url += `${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  }
  return url;
};

/** Build per-punch selfie API URL (multi-punch attendance) */
export const buildPunchPhotoUrl = (punchId) => {
  if (!punchId) return null;
  const apiRoot = getApiRoot();
  const token = localStorage.getItem('erp_auth_token');
  let url = `${apiRoot}/attendance/punch/${punchId}/photo`;
  if (token) {
    url += `${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  }
  return url;
};

/** Resolve upload/API media paths to absolute URLs for img src */
export const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;

  // API paths like /api/attendance/... — attach to API root directly
  if (url.startsWith('/api/')) {
    let fullUrl = `${getApiRoot()}${url.slice(4)}`;
    if (fullUrl.includes('/photo/') && !fullUrl.includes('token=')) {
      const token = localStorage.getItem('erp_auth_token');
      if (token) {
        fullUrl += `${fullUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      }
    }
    return fullUrl;
  }

  const base = getApiBaseUrl();
  const path = url.replace(/^\//, '');
  let fullUrl = `${base}/${path}`;

  if (path.startsWith('api/attendance/') && path.includes('/photo/') && !fullUrl.includes('token=')) {
    const token = localStorage.getItem('erp_auth_token');
    if (token) {
      fullUrl += `${fullUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
    }
  }

  return fullUrl;
};

/** Normalized punch list for a daily attendance record (multi-punch or legacy) */
export const getRecordPunchList = (record) => {
  if (!record) return [];

  if (record.punches?.length) {
    let inCount = 0;
    let outCount = 0;
    return record.punches.map((punch) => {
      const isIn = punch.punch_type === 'IN';
      const seq = isIn ? ++inCount : ++outCount;
      return {
        id: punch.id,
        type: punch.punch_type,
        seq,
        time: punch.punch_time,
        location: punch.location_address,
        photoUrl: punch.photo_url,
        latitude: punch.latitude,
        longitude: punch.longitude,
        attendanceDate: record.attendance_date,
        recordId: record.id,
      };
    });
  }

  const legacy = [];
  if (record.clock_in_time) {
    legacy.push({
      id: `${record.id}-in`,
      type: 'IN',
      seq: 1,
      time: record.clock_in_time,
      location: record.clock_in_location_address,
      photoUrl: record.clock_in_photo_url,
      latitude: record.clock_in_latitude,
      longitude: record.clock_in_longitude,
      attendanceDate: record.attendance_date,
      recordId: record.id,
    });
  }
  if (record.clock_out_time) {
    legacy.push({
      id: `${record.id}-out`,
      type: 'OUT',
      seq: 1,
      time: record.clock_out_time,
      location: record.clock_out_location_address,
      photoUrl: record.clock_out_photo_url,
      latitude: record.clock_out_latitude,
      longitude: record.clock_out_longitude,
      attendanceDate: record.attendance_date,
      recordId: record.id,
    });
  }
  return legacy;
};
